import classNames from 'classnames';
import styles from './AcademyList.module.scss';
import { useEffect, useState } from 'react';
import { BlurbCard, Grid, Heading, Button, AcademyFilters, PageSpinner, Paragraph } from 'ui/components';
import formatAcademyEntryType from 'helpers/formatAcademyEntryType';

export interface AcademyListProps {
	className?: string;
	heading?: string;
	filterOptions?: Models.AcademyFilters;
	academyEntries?: { year: number; items: Models.AcademyEntry[] }[];
	showLoadMore?: boolean;
	dictionary?: { [key: string]: string };
	selectedAcademyType?: string;
	handleFilterLanguageChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
	selectedAcademyLanguage?: string;
	handleFilterTypeChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
	totalResults?: number;
	pageSize?: number;
	loading?: boolean;
	linkToEntry: (item: Models.AcademyEntry) => {
		linkType?: string;
		url: string;
		name: string;
		target?: string;
	};
}

export const AcademyList: React.FC<AcademyListProps> = ({
	heading,
	className,
	filterOptions,
	academyEntries,
	dictionary,
	showLoadMore = true,
	totalResults = 0,
	selectedAcademyLanguage,
	handleFilterLanguageChange,
	selectedAcademyType,
	handleFilterTypeChange,
	pageSize,
	loading,
	linkToEntry,
}) => {
	const [visible, setVisible] = useState(pageSize);

	// Reset when filters change
	useEffect(() => {
		setVisible(pageSize);
	}, [selectedAcademyType, selectedAcademyLanguage, pageSize]);

	// Flattened all grouped items for easier slicing
	const flattened = (academyEntries || []).flatMap((g) =>
		g.items.map((item) => ({
			...item,
			year: g.year,
		})),
	);

	const visibleItems = flattened.slice(0, visible);
	const canLoadMore = visible < flattened.length;

	// Group visible items back to show 4 at a time
	const visibleGroupedMap = new Map<number, Models.AcademyEntry[]>();
	for (const item of visibleItems) {
		if (!visibleGroupedMap.has(item.year)) visibleGroupedMap.set(item.year, []);
		visibleGroupedMap.get(item.year)!.push(item);
	}
	const visibleGrouped = Array.from(visibleGroupedMap.entries()).map(([year, items]) => ({ year, items }));

	return (
		<Grid className={classNames(styles.AcademyList, className)}>
			{heading && (
				<Heading headingLevel="h2" className={styles.AcademyList_heading}>
					{heading}
				</Heading>
			)}

			{filterOptions && (
				<AcademyFilters
					className={styles.AcademyList_filters}
					filterOptions={filterOptions}
					dictionary={dictionary}
					selectedAcademyLanguage={selectedAcademyLanguage}
					selectedAcademyType={selectedAcademyType}
					handleLanguageChange={handleFilterLanguageChange}
					handleTypeChange={handleFilterTypeChange}
				/>
			)}
			{visibleGrouped?.map((group) => (
				<section key={group.year} className={styles.AcademyList_yearGroup}>
					<Heading headingLevel="h3" className={styles.AcademyList_yearHeading}>
						{group.year}
					</Heading>

					<ul className={styles.AcademyList_itemsContainer} aria-live="polite">
						{group.items.map((item) => (
							<li className={styles.AcademyList_item} key={item?.id} id={item?.id}>
								<BlurbCard
									variant="website"
									style="bordered"
									className={styles.AcademyList_card}
									academyCardType={formatAcademyEntryType(item?.type?.name) || 'default'}
									kicker={item?.type?.displayName ?? item?.type?.name}
									text={item?.teaserText}
									date={item?.startDateTime}
									localizedDate={item?.localizedStartDateTime}
									language={item?.language?.displayName ?? item?.language?.name}
									location={item?.hideLocation ? null : item?.location}
									heading={item?.title}
									dictionary={dictionary}
									link={linkToEntry?.(item)}
								/>
							</li>
						))}
					</ul>
				</section>
			))}

			{showLoadMore && canLoadMore && (
				<Button style="secondaryWeb" className={styles.AcademyList_loadMoreButton} onClick={() => setVisible((v) => Math.min(v + pageSize))}>
					{dictionary?.loadMoreButtonText}
				</Button>
			)}

			{!showLoadMore && totalResults <= 1 && (
				<Paragraph variant="website" style="lg" className={styles.AcademyList_resultText}>
					{totalResults} {dictionary?.resultsFoundText}
				</Paragraph>
			)}
		</Grid>
	);
};
